/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

/* eslint class-methods-use-this: ["error", { "exceptMethods": ["getDates"] }] */

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import * as CalendarActions from '../actions/calendar';
import Calendar from '../components/calendar';
import mcLib from '../api/mc-lib';

const week = [0, 1, 2, 3, 4, 5, 6];
function getWeekOfDate(date = new Date()) {
  const first = date.getDate() - date.getDay();
  date.setHours(0, 0, 0, 0);

  return week.map((day) => {
    const weekDate = new Date(date);
    weekDate.setDate(first + day);
    return weekDate;
  });
}
function getDates() {
  const selectedDate = new Date(); // Today
  selectedDate.setHours(0, 0, 0, 0);

  const dates = getWeekOfDate(selectedDate); // Current week
  const startDate = dates[0];
  const endDate = dates[dates.length - 1];

  return {
    selectedDate,
    startDate,
    endDate,
    dates,
  };
}

function handleExpandClick() {
  mcLib.logEvent('launch calendar', 'fullscreen button');
  mcLib.appDeeplink({ appName: 'Calendar' });
}

export function CalendarContainer(props) {
  const previousProps = useRef(props);
  const [state, setState] = useState(getDates());
  // STATE: startDate, endDate, dates, selectedDate
  const getAppProjectedEventsData = useCallback(
    ({ hasCalendarAccess, projectedEventsPage, projectedEventsPageSize } = props) => {
      if (hasCalendarAccess) {
        const newState = getDates();
        setState(getDates());
        const { startDate, endDate } = newState;
        props.calendarActions.getProjectedSends({
          startDate,
          endDate,
          projectedEventsPage,
          projectedEventsPageSize,
        });
      } else {
        props.calendarActions.hasCalendarAccess();
      }
    },
    [props.calendarActions]
  );

  const getAppData = useCallback(
    ({ hasCalendarAccess } = props) => {
      if (hasCalendarAccess) {
        const newState = getDates();
        setState(newState);
        const { startDate, endDate } = newState;
        props.calendarActions.getCalendarEvents({
          startDate,
          endDate,
        });
      } else {
        props.calendarActions.hasCalendarAccess();
      }
    },
    [props.calendarActions]
  );

  useEffect(() => {
    getAppData(props);
    getAppProjectedEventsData(props);
  }, []);
  useEffect(() => {
    const prevProps = previousProps.current;

    if (props.hasCalendarAccess && !prevProps.hasCalendarAccess) {
      getAppData(props);
      getAppProjectedEventsData(props);
    } else if (props.projectedEventsPage && props.projectedEventsPage !== prevProps.projectedEventsPage) {
      getAppProjectedEventsData(props);
    }

    previousProps.current = props;
  }, [props]);

  if (props.hasCalendarAccess) {
    return (
      <Calendar
        dates={state.dates}
        endDate={state.endDate}
        events={props.calendarEvents}
        localizations={props.localizations}
        onExpandClick={handleExpandClick}
        selectedDate={state.selectedDate}
        startDate={state.startDate}
      />
    );
  }
  return null;
}

CalendarContainer.displayName = 'Calendar Container';

CalendarContainer.propTypes = {
  calendarActions: PropTypes.object,
  calendarEvents: PropTypes.array,
  hasCalendarAccess: PropTypes.bool,
  projectedEventsPage: PropTypes.number,
  projectedEventsPageSize: PropTypes.number,
  localizations: PropTypes.object,
};

function mapStateToProps(state) {
  const calendarEvents = state.calendar.items || [];

  return {
    calendarEvents,
    projectedEventsPage: state.calendar.projectedEventsPage,
    projectedEventsPageSize: state.calendar.projectedEventsPageSize,
    hasCalendarAccess: state.permissions.hasCalendarAccess,
    localizations: state.localization.localizations,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    calendarActions: bindActionCreators(CalendarActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CalendarContainer);
